import { c as _c } from "react/compiler-runtime";
import { ArrowLeft } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { api, type ShopMember, type Storefront, type User, type UserPatch, UserRole } from "@/api";
import { usePlatformRoles } from "@/api/hooks/use-platform-roles";
import { useStorefronts } from "@/api/hooks/use-storefronts";
import { useBuyerAnalytics, userKeys, useUser, useUserStaff } from "@/api/hooks/use-users";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { SellerSurvey } from "@/components/seller-survey";
import { StackedThumbs } from "@/components/stacked-thumbs";
import { Badge } from "@osmix/ui/badge";
import { Button } from "@osmix/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@osmix/ui/card";
import { Input } from "@osmix/ui/input";
import { Label } from "@osmix/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { Skeleton } from "@osmix/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { hasAdminPermission } from "@/lib/admin-permissions";
import { orderNo } from "@/lib/short-numbers";
import { formatDateTime } from "@/lib/utils";
import { useAuth } from "@/store";

// Roles that filled in a seller intake survey. Shop verification and the
// onboarding/moderation lifecycle are a shop concern — they live on the Shops
// surface (see pages/seller-detail.tsx). The user page only shows the seller's
// own survey and a read-only onboarding status with a link there.
const SELLER_ROLES: UserRole[] = ["seller", "verified_seller"];
type Draft = {
  name: string;
  phone: string;
  role: UserRole;
  platformRoleKey: string | null;
};
const toDraft = (u: User): Draft => ({
  name: u.name,
  phone: u.phone,
  role: u.role,
  platformRoleKey: u.platformRole?.key ?? null
});
export function UserDetailPage() {
  const $ = _c(2);
  const {
    id: t0
  } = useParams();
  const id = t0 === undefined ? "" : t0;
  let t1;
  if ($[0] !== id) {
    t1 = <UserDetailContent id={id} showBack={true} />;
    $[0] = id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
export function UserDetailContent({
  id,
  showBack = false
}: {
  id: string;
  showBack?: boolean;
}) {
  const {
    t
  } = useTranslation();
  const adminUser = useAuth(s => s.user);
  const selfId = adminUser?.id;
  const queryClient = useQueryClient();
  const userQuery = useUser(id);
  const staffQuery = useUserStaff(id);
  const storefrontsQuery = useStorefronts();
  // Platform roles are optional context — a load failure just leaves the
  // select with the "none" option (the original fetch swallowed errors too).
  const platformRolesQuery = usePlatformRoles();
  const user = userQuery.data ?? null;
  const memberships = staffQuery.data ?? [];
  const storefronts = storefrontsQuery.data ?? [];
  const platformRoles = platformRolesQuery.data ?? [];
  const [draft, setDraft] = useState<Draft | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordSaved, setPasswordSaved] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const loadError = userQuery.error ?? staffQuery.error ?? storefrontsQuery.error;
  const error = actionError ?? (loadError ? loadError.message : null);

  // Clear per-user transient state when the id changes (the mount pass is a
  // no-op — everything is still at its initial value).
  const resetFor = useRef(id);
  useEffect(() => {
    if (resetFor.current === id) return;
    resetFor.current = id;
    setActionError(null);
    setPassword("");
    setPasswordConfirm("");
    setPasswordError(null);
    setPasswordSaved(false);
  }, [id]);

  // Seed the editable draft once per user id, from the fetched user — later
  // background refetches must not wipe in-progress edits.
  const seededFor = useRef<string | null>(null);
  useEffect(() => {
    if (!user || seededFor.current === id) return;
    seededFor.current = id;
    setDraft(toDraft(user));
  }, [user, id]);
  const roleLabel = (r: string) => t(`roles.${r}`, {
    defaultValue: r
  });
  const statusLabel = (s_0: string) => t(`onboarding.${s_0}`, {
    defaultValue: s_0
  });
  const set = <K extends keyof Draft,>(key: K) => (value: Draft[K]) => {
    setDraft(d => d ? {
      ...d,
      [key]: value
    } : d);
    setSaved(false);
  };
  const isSelf = !!selfId && selfId === id;
  const dirty = !!user && !!draft && JSON.stringify(draft) !== JSON.stringify(toDraft(user));
  const valid = !!draft && draft.name.trim().length > 0 && draft.phone.trim().length > 0;
  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setActionError(null);
    try {
      const patch: UserPatch = {
        name: draft.name.trim(),
        phone: draft.phone.trim(),
        role: draft.role,
        platformRoleKey: draft.platformRoleKey
      };
      const updated = await api.users.update(id, patch);
      queryClient.setQueryData(userKeys.detail(id), updated);
      void queryClient.invalidateQueries({
        queryKey: userKeys.all
      });
      setDraft(toDraft(updated));
      setSaved(true);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };
  const changePassword = async () => {
    setPasswordError(null);
    setPasswordSaved(false);
    const nextPassword = password.trim();
    if (nextPassword.length < 8) {
      setPasswordError(t("users.detail.passwordRequired"));
      return;
    }
    if (nextPassword !== passwordConfirm.trim()) {
      setPasswordError(t("users.detail.passwordMismatch"));
      return;
    }
    setPasswordSaving(true);
    try {
      const updated_0 = await api.users.changePassword(id, {
        password: nextPassword
      });
      queryClient.setQueryData(userKeys.detail(id), updated_0);
      setPassword("");
      setPasswordConfirm("");
      setPasswordSaved(true);
    } catch (e_0) {
      setPasswordError(e_0 instanceof Error ? e_0.message : String(e_0));
    } finally {
      setPasswordSaving(false);
    }
  };
  return <div className="flex max-w-4xl flex-col gap-6">
			<div className="flex items-center gap-3">
				{showBack && <Button variant="outline" size="sm" asChild>
						<Link to="/users">
							<ArrowLeft className="size-4" /> {t("users.detail.back")}
						</Link>
					</Button>}
				{user && <Badge variant="secondary">{roleLabel(user.role)}</Badge>}
			</div>

			{error && <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
					{error}
				</div>}

			{!draft && !error && <div className="space-y-4">
					<Skeleton className="h-8 w-2/3" />
					<Skeleton className="h-48 w-full" />
				</div>}

			{user && draft && <>
					<header className="space-y-1">
						<h1 className="text-2xl font-semibold">{user.name}</h1>
						<p className="text-sm text-muted-foreground">
							{user.number ? <>
									<span className="tabular-nums" title={user.id}>
										{t("users.detail.number", {
                number: user.number
              })}
									</span>
									{" · "}
								</> : null}
							{user.email} ·{" "}
							{t("users.detail.createdAt", {
            time: formatDateTime(user.createdAt)
          })}
						</p>
					</header>

					<Card>
						<CardHeader>
							<CardTitle>{t("users.detail.account")}</CardTitle>
						</CardHeader>
						<CardContent className="grid gap-4 sm:grid-cols-2">
							<Field label={t("users.field.name")}>
								<Input value={draft.name} onChange={e_1 => set("name")(e_1.target.value)} />
							</Field>
							<Field label={t("users.field.phone")}>
								<Input value={draft.phone} onChange={e_2 => set("phone")(e_2.target.value)} />
							</Field>
							<Field label={t("users.field.email")}>
								<Input value={user.email} disabled readOnly />
							</Field>
						</CardContent>
					</Card>

					<Card>
						<CardHeader>
							<CardTitle>{t("users.detail.access")}</CardTitle>
						</CardHeader>
						<CardContent className="grid gap-4 sm:grid-cols-2">
							<Field label={t("users.field.role")} hint={isSelf ? t("users.detail.selfRoleHint") : undefined}>
								<Select value={draft.role} disabled={isSelf} onChange={v => set("role")(v as UserRole)} options={UserRole.options.map(r_0 => ({
              value: r_0,
              label: roleLabel(r_0)
            }))} />
							</Field>
							<Field label={t("users.field.platformRole")}>
								<Select value={draft.platformRoleKey ?? ""} onChange={v_0 => set("platformRoleKey")(v_0 || null)} options={[{
              value: "",
              label: t("users.platformRole.none")
            }, ...platformRoles.map(role => ({
              value: role.key,
              label: role.name
            }))]} />
							</Field>
							<Field label={t("users.field.onboarding")} hint={SELLER_ROLES.includes(user.role) ? undefined : t("users.detail.onboardingReadOnly")}>
								<div className="flex h-9 items-center gap-3">
									<Badge variant="secondary">
										{statusLabel(user.onboardingStatus)}
									</Badge>
									{SELLER_ROLES.includes(user.role) && <Link to={`/shops/seller/${user.id}`} className="text-sm text-primary hover:underline">
											{t("users.detail.moderate")}
										</Link>}
								</div>
							</Field>
						</CardContent>
					</Card>

					{hasAdminPermission(adminUser, "action.users.manage") && !isSelf && user.role !== "admin" && <UserBlockCard user={user} />}

					<Card>
						<CardHeader>
							<CardTitle>{t("users.detail.password")}</CardTitle>
						</CardHeader>
						<CardContent className="grid gap-4 sm:grid-cols-2">
							<Field label={t("users.detail.newPassword")} hint={t("users.detail.passwordHint")}>
								<PasswordInput value={password} onChange={event => {
              setPassword(event.target.value);
              setPasswordError(null);
              setPasswordSaved(false);
            }} showLabel={t("users.password.show")} hideLabel={t("users.password.hide")} />
							</Field>
							<Field label={t("users.detail.confirmPassword")}>
								<PasswordInput value={passwordConfirm} onChange={event_0 => {
              setPasswordConfirm(event_0.target.value);
              setPasswordError(null);
              setPasswordSaved(false);
            }} showLabel={t("users.password.show")} hideLabel={t("users.password.hide")} />
							</Field>
							<div className="flex items-center gap-3 sm:col-span-2">
								<Button onClick={changePassword} disabled={passwordSaving || !password || !passwordConfirm}>
									{passwordSaving ? t("users.detail.changingPassword") : t("users.detail.changePassword")}
								</Button>
								{passwordSaved && <span className="text-sm text-muted-foreground">{t("users.detail.passwordChanged")}</span>}
								{passwordError && <span className="text-sm text-destructive">{passwordError}</span>}
							</div>
						</CardContent>
					</Card>

					<div className="flex items-center gap-3">
						<Button onClick={save} disabled={saving || !dirty || !valid}>
							{saving ? t("users.detail.saving") : t("users.detail.save")}
						</Button>
						{saved && !dirty && <span className="text-sm text-muted-foreground">
								{t("users.detail.saved")}
							</span>}
					</div>

					<UserShopsCard memberships={memberships} storefronts={storefronts} />

					{SELLER_ROLES.includes(user.role) && <SellerSurvey userId={user.id} />}

					{user.role === "buyer" && <BuyerAnalyticsCard userId={user.id} />}
				</>}
		</div>;
}
function BuyerAnalyticsCard(t0) {
  const $ = _c(31);
  const {
    userId
  } = t0;
  const {
    t
  } = useTranslation();
  const query = useBuyerAnalytics(userId, true);
  const a = query.data;
  let t1;
  if ($[0] !== t) {
    t1 = t("users.buyer.active", {
      defaultValue: "\u0410\u043A\u0442\u0438\u0432\u043D\u044B\u0439"
    });
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t) {
    t2 = t("users.buyer.dormant", {
      defaultValue: "\u0414\u0430\u0432\u043D\u043E \u043D\u0435 \u043F\u043E\u043A\u0443\u043F\u0430\u043B"
    });
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t) {
    t3 = t("users.buyer.new", {
      defaultValue: "\u0411\u0435\u0437 \u043F\u043E\u043A\u0443\u043F\u043E\u043A"
    });
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = {
      active: t1,
      dormant: t2,
      new: t3
    };
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const activityLabel = t4;
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      active: "text-emerald-600",
      dormant: "text-amber-600",
      new: "text-muted-foreground"
    };
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const activityTone = t5;
  const money = _temp;
  let t6;
  if ($[11] !== t) {
    t6 = t("users.buyer.title", {
      defaultValue: "\u041F\u043E\u043A\u0443\u043F\u0430\u0442\u0435\u043B\u044C: \u0430\u043A\u0442\u0438\u0432\u043D\u043E\u0441\u0442\u044C \u0438 \u0438\u0441\u0442\u043E\u0440\u0438\u044F"
    });
    $[11] = t;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t6) {
    t7 = <CardHeader><CardTitle>{t6}</CardTitle></CardHeader>;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== query.isLoading || $[16] !== t) {
    t8 = query.isLoading && <p className="text-sm text-muted-foreground">{t("common.loading", {
        defaultValue: "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430\u2026"
      })}</p>;
    $[15] = query.isLoading;
    $[16] = t;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== query.error) {
    t9 = query.error && <p className="text-sm text-destructive">{query.error.message}</p>;
    $[18] = query.error;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== a || $[21] !== activityLabel || $[22] !== t) {
    t10 = a && <div className="flex flex-col gap-4"><div className="grid gap-3 sm:grid-cols-4"><div className="rounded-lg border p-3"><div className={`text-lg font-semibold ${activityTone[a.activity]}`}>{activityLabel[a.activity]}</div><div className="text-xs text-muted-foreground">{a.daysSinceLastOrder != null ? t("users.buyer.lastOrderDays", {
              count: a.daysSinceLastOrder,
              defaultValue: `${a.daysSinceLastOrder} дн. назад`
            }) : t("users.buyer.noOrders", {
              defaultValue: "\u0437\u0430\u043A\u0430\u0437\u043E\u0432 \u043D\u0435\u0442"
            })}</div></div><div className="rounded-lg border p-3"><div className="text-lg font-semibold tabular-nums">{money(a.gmv)}</div><div className="text-xs text-muted-foreground">{t("users.buyer.gmv", {
              defaultValue: "\u0421\u0443\u043C\u043C\u0430 \u043F\u043E\u043A\u0443\u043F\u043E\u043A"
            })}</div></div><div className="rounded-lg border p-3"><div className="text-lg font-semibold tabular-nums">{money(a.avgCheck)}</div><div className="text-xs text-muted-foreground">{t("users.buyer.avgCheck", {
              defaultValue: "\u0421\u0440\u0435\u0434\u043D\u0438\u0439 \u0447\u0435\u043A"
            })}</div></div><div className="rounded-lg border p-3"><div className="text-lg font-semibold tabular-nums">{a.ordersPaid}{a.ordersCancelled > 0 && <span className="text-sm text-muted-foreground"> +{a.ordersCancelled} отм.</span>}</div><div className="text-xs text-muted-foreground">{t("users.buyer.orders", {
              defaultValue: "\u041E\u043F\u043B\u0430\u0447\u0435\u043D\u043D\u044B\u0445 \u0437\u0430\u043A\u0430\u0437\u043E\u0432"
            })}</div></div></div><div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-muted-foreground">{a.firstOrderAt && <span>{t("users.buyer.firstOrder", {
            defaultValue: "\u041F\u0435\u0440\u0432\u044B\u0439 \u0437\u0430\u043A\u0430\u0437"
          })}:{" "}{new Date(a.firstOrderAt).toLocaleDateString("ru-RU")}</span>}{a.lastOrderAt && <span>{t("users.buyer.lastOrder", {
            defaultValue: "\u041F\u043E\u0441\u043B\u0435\u0434\u043D\u0438\u0439 \u0437\u0430\u043A\u0430\u0437"
          })}:{" "}{new Date(a.lastOrderAt).toLocaleDateString("ru-RU")}</span>}<span>{t("users.buyer.ordersTotal", {
            defaultValue: "\u0412\u0441\u0435\u0433\u043E \u0437\u0430\u043A\u0430\u0437\u043E\u0432"
          })}: {a.ordersTotal}</span></div>{a.topShops.length > 0 && <div className="flex flex-wrap items-center gap-2"><span className="text-xs text-muted-foreground">{t("users.buyer.topShops", {
            defaultValue: "\u041B\u044E\u0431\u0438\u043C\u044B\u0435 \u043C\u0430\u0433\u0430\u0437\u0438\u043D\u044B"
          })}:</span>{a.topShops.map(_temp2)}</div>}{a.orders.length > 0 ? <Table><TableHeader><TableRow><TableHead>{t("users.buyer.order", {
                defaultValue: "\u0417\u0430\u043A\u0430\u0437"
              })}</TableHead><TableHead>{t("users.buyer.date", {
                defaultValue: "\u0414\u0430\u0442\u0430"
              })}</TableHead><TableHead>{t("products.col.photo", {
                defaultValue: "\u0422\u043E\u0432\u0430\u0440\u044B"
              })}</TableHead><TableHead>{t("users.buyer.shopsCol", {
                defaultValue: "\u041C\u0430\u0433\u0430\u0437\u0438\u043D\u044B"
              })}</TableHead><TableHead>{t("users.buyer.statusCol", {
                defaultValue: "\u0421\u0442\u0430\u0442\u0443\u0441"
              })}</TableHead><TableHead className="text-right">{t("users.buyer.sum", {
                defaultValue: "\u0421\u0443\u043C\u043C\u0430"
              })}</TableHead></TableRow></TableHeader><TableBody>{a.orders.slice(0, 20).map(o => <TableRow key={o.id}><TableCell className="whitespace-nowrap tabular-nums" title={o.id}><Link to={`/orders?order=${encodeURIComponent(o.id)}`} className="text-primary hover:underline">{orderNo(o.id, o.number)}</Link></TableCell><TableCell className="text-sm">{new Date(o.createdAt).toLocaleDateString("ru-RU")}</TableCell><TableCell><StackedThumbs items={o.thumbnails.map(_temp3)} /></TableCell><TableCell className="text-sm">{o.shops.join(", ")}</TableCell><TableCell><Badge variant="secondary" className="font-normal">{t(`orders.status.${o.status}`, {
                  defaultValue: o.status
                })}</Badge></TableCell><TableCell className="text-right tabular-nums">{money(o.total)}</TableCell></TableRow>)}</TableBody></Table> : <p className="text-sm text-muted-foreground">{t("users.buyer.noOrders", {
          defaultValue: "\u0423 \u043F\u043E\u043A\u0443\u043F\u0430\u0442\u0435\u043B\u044F \u0435\u0449\u0451 \u043D\u0435\u0442 \u0437\u0430\u043A\u0430\u0437\u043E\u0432"
        })}</p>}</div>;
    $[20] = a;
    $[21] = activityLabel;
    $[22] = t;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t8 || $[26] !== t9) {
    t11 = <CardContent>{t8}{t9}{t10}</CardContent>;
    $[24] = t10;
    $[25] = t8;
    $[26] = t9;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== t11 || $[29] !== t7) {
    t12 = <Card>{t7}{t11}</Card>;
    $[28] = t11;
    $[29] = t7;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  return t12;
}
function _temp3(url) {
  return {
    photo: url,
    name: ""
  };
}
function _temp2(sh) {
  return <Badge key={sh.shopName} variant="secondary" className="font-normal">{sh.shopName} · {sh.orders}</Badge>;
}
function _temp(n) {
  return `${Math.round(n).toLocaleString("ru-RU")} ₽`;
}
function UserShopsCard(t0) {
  const $ = _c(13);
  const {
    memberships,
    storefronts
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] !== storefronts) {
    t1 = new Map(storefronts.map(_temp4));
    $[0] = storefronts;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const byId = t1;
  let t2;
  if ($[2] !== t) {
    t2 = t("users.detail.shops");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <CardHeader><CardTitle>{t2}</CardTitle></CardHeader>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== byId || $[7] !== memberships || $[8] !== t) {
    t4 = <CardContent>{memberships.length === 0 ? <p className="text-sm text-muted-foreground">{t("users.detail.noShops")}</p> : <Table><TableHeader><TableRow><TableHead>{t("users.detail.shop")}</TableHead><TableHead>{t("users.field.role")}</TableHead><TableHead>{t("staff.status")}</TableHead></TableRow></TableHeader><TableBody>{memberships.map(member => {
            const shop_0 = byId.get(member.shopId);
            const label = shop_0?.name ?? member.shopId;
            return <TableRow key={member.id}><TableCell className="font-medium">{shop_0 ? <Link to={`/shops/${shop_0.slug}`} className="hover:underline">{label}</Link> : label}</TableCell><TableCell>{t(`roles.${member.role}`)}</TableCell><TableCell><Badge variant={member.status === "active" ? "secondary" : "outline"}>{t(`staff.statuses.${member.status}`)}</Badge></TableCell></TableRow>;
          })}</TableBody></Table>}</CardContent>;
    $[6] = byId;
    $[7] = memberships;
    $[8] = t;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = <Card>{t3}{t4}</Card>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}
function _temp4(shop) {
  return [shop.id, shop];
}
function Field(t0) {
  const $ = _c(8);
  const {
    label,
    hint,
    children
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <Label>{label}</Label>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== hint) {
    t2 = hint && <p className="text-xs text-muted-foreground">{hint}</p>;
    $[2] = hint;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== children || $[5] !== t1 || $[6] !== t2) {
    t3 = <div className="flex flex-col gap-1.5">{t1}{children}{t2}</div>;
    $[4] = children;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function Select(t0) {
  const $ = _c(9);
  const {
    value,
    options,
    onChange,
    disabled
  } = t0;
  let t1;
  if ($[0] !== onChange) {
    t1 = e => onChange(e.target.value);
    $[0] = onChange;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== options) {
    t2 = options.map(_temp5);
    $[2] = options;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== disabled || $[5] !== t1 || $[6] !== t2 || $[7] !== value) {
    t3 = <select className="h-9 rounded-md border bg-transparent px-3 text-sm text-foreground shadow-xs disabled:cursor-not-allowed disabled:opacity-50" value={value} disabled={disabled} onChange={t1}>{t2}</select>;
    $[4] = disabled;
    $[5] = t1;
    $[6] = t2;
    $[7] = value;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}

// Блокировка пользователя площадкой: вход закрыт, сессии отозваны. Причина
// обязательна — по ней потом разбираются, если человек напишет в поддержку.
function _temp5(o) {
  return <option key={o.value} value={o.value}>{o.label}</option>;
}
function UserBlockCard({
  user
}: {
  user: User;
}) {
  const {
    t
  } = useTranslation();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (block: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const next = block ? await api.users.block(user.id, reason.trim()) : await api.users.unblock(user.id);
      queryClient.setQueryData(userKeys.detail(user.id), next);
      await queryClient.invalidateQueries({
        queryKey: userKeys.all
      });
      setReason("");
      setConfirm(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setConfirm(false);
    } finally {
      setBusy(false);
    }
  };
  return <Card data-testid="user-block-card">
			<CardHeader>
				<CardTitle>{t("users.block.title")}</CardTitle>
			</CardHeader>
			<CardContent className="flex flex-col gap-3 text-sm">
				{user.blockedAt ? <>
						<p className="text-destructive">
							{t("users.block.blockedSince", {
            at: formatDateTime(user.blockedAt)
          })}
						</p>
						{user.blockedReason && <p className="text-muted-foreground">«{user.blockedReason}»</p>}
						<Button variant="outline" className="self-start" disabled={busy} onClick={() => void run(false)}>
							{t("users.block.unblock")}
						</Button>
					</> : <>
						<p className="text-muted-foreground">{t("users.block.hint")}</p>
						<div className="flex flex-wrap gap-2">
							<Input value={reason} onChange={event => setReason(event.target.value)} placeholder={t("users.block.reasonPlaceholder")} className="min-w-0 flex-1" />
							<Button variant="destructive" disabled={busy || !reason.trim()} onClick={() => setConfirm(true)}>
								{t("users.block.block")}
							</Button>
						</div>
					</>}
				{error && <p className="text-destructive">{error}</p>}
				<ConfirmDialog open={confirm} title={t("users.block.confirmTitle", {
        name: user.name || user.phone
      })} description={t("users.block.confirmDescription")} confirmVariant="destructive" busy={busy} onConfirm={() => void run(true)} onClose={() => setConfirm(false)} />
			</CardContent>
		</Card>;
}